import { array, option, string } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { deburr, includes, intersection, isEqual, noop, size, toLower, trim } from 'lodash/fp';
import pt from 'prop-types';
import { Children, cloneElement, Component, Fragment, isValidElement } from 'react';
import { GroupOption, Option } from '@mews-commander/types';
import { UnicodeEntity } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Divider, DropdownItem, IconName, ListHeadline, Select as OptimusSelect, Size } from '@optimus-web/core';
import { TooltippedIcon } from '../../../tooltip';
import { CommanderFormContext } from '../../common';

const EMPTY_SELECTION: $TSFixMe = [];

export const selectedPropType = pt.oneOfType([pt.string, pt.number]);

const isGroupOption = (options: Array<GroupOption> | Array<Option>): options is Array<GroupOption> =>
    (options[0] as GroupOption)?.groupOptions !== undefined;

const mapActiveValues = flow(
    (options: Array<GroupOption> | Array<Option>) =>
        isGroupOption(options) ? options.flatMap(({ groupOptions }) => groupOptions) : options,
    array.filterMap(({ disabled, value }) => (!disabled ? option.some(value) : option.none))
);

interface OwnProps {
    // @ts-expect-error $TSFixMeError: 'selectedPropType' refers to a value, but is being used as a type here. Did you mean 'typeof selectedPropType'?
    selected?: selectedPropType | selectedPropType[];
    options?: Array<Option> | Array<GroupOption>;
    required?: boolean;
    disabled?: boolean;
    nullText?: React.ReactNode;
    onChange?: $TSFixMeFunction;
    multiselect?: boolean;
    shadowField?: boolean;
    name?: string;
    virtualized?: boolean;
    showSelectAll?: boolean;
    breakpointRange?: $TSFixMe; // TODO: pt.instanceOf(BreakpointRange)
    innerRef?: $TSFixMeFunction;
    showEmptyValue?: boolean;
    defaultItemHeight?: number;
    searchPlaceholder?: React.ReactNode;
    placeholder?: React.ReactNode;
    label?: React.ReactNode;
    size?: Size;
    helper?: React.ReactNode;
    bottomCaption?: React.ReactNode;
    error?: React.ReactNode;
    inline?: boolean;
    readOnly?: boolean;
}

type State = $TSFixMe;

type Props = OwnProps &
    typeof Select.defaultProps & {
        children?: React.ReactNode;
    };

export class Select extends Component<Props, State> {
    static defaultProps = {
        nullText: UnicodeEntity.Dash,
        onChange: noop,
        defaultItemHeight: 40,
    };

    allValues: $TSFixMe;
    declare context: React.ContextType<typeof CommanderFormContext>;

    constructor(props: Props) {
        super(props);
        this.allValues = props.options && mapActiveValues(props.options);

        this.state = {
            allSelected: this.hasAllSelected(props),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
        if (!isEqual(this.props.options, nextProps.options)) {
            this.allValues = mapActiveValues(nextProps.options);

            let finalSelection;

            if (this.props.multiselect) {
                finalSelection = intersection(this.allValues, nextProps.selected);
            } else {
                const allValues = this.props.showEmptyValue ? [string.empty, ...this.allValues] : this.allValues;
                const fallbackValue = this.props.showEmptyValue ? string.empty : null;
                finalSelection = includes(nextProps.selected, allValues) ? nextProps.selected : fallbackValue;
            }

            this.handleChange(null, finalSelection);
        }
    }

    handleChange = (e: $TSFixMe, selected: $TSFixMe) => {
        const allSelected = this.hasAllSelected({ multiselect: this.props.multiselect, selected });
        this.setState({ allSelected });

        this.props.onChange(selected);
    };

    handleSelectAllClick = () => {
        const { allSelected } = this.state;
        const selected = allSelected ? EMPTY_SELECTION : this.allValues;
        this.setState({ allSelected: !allSelected });
        this.props.onChange(selected);
    };

    hasAllSelected = ({ multiselect, selected }: $TSFixMe) =>
        multiselect && size(intersection(this.allValues, selected)) === this.allValues.length;

    renderSingleItems = array.mapWithIndex((index, { value, label, secondaryText, disabled }: Option) => (
        <DropdownItem
            data-test-select-item-value={value}
            key={index}
            value={value ?? undefined}
            label={label}
            description={secondaryText}
            disabled={disabled}
            searchValue={typeof label === 'string' && label?.length ? flow(deburr, toLower, trim)(label) : undefined}
        />
    ));

    renderGroupItems = array.mapWithIndex((index, { groupOptions, groupLabel }: GroupOption) => {
        const menuItems = this.renderSingleItems(groupOptions);
        return [
            // the fragments here are necessary to bypass the console error 'Unknown event handler property `onAfterClick`'
            index === 0 ? null : (
                <Fragment key="section-divider">
                    <Divider />
                </Fragment>
            ),
            <Fragment key="headline">
                <ListHeadline>{groupLabel}</ListHeadline>
            </Fragment>,
            ...menuItems,
        ];
    });

    render() {
        const {
            options,
            name,
            multiselect,
            children,
            virtualized,
            showSelectAll,
            selected,
            breakpointRange,
            showEmptyValue,
            nullText,
            helper,
            bottomCaption,
            ...other
        } = this.props;

        const itemCount = (options ? options.length : 0) + Children.count(children);

        const captionIconWithTooltip = helper ? (
            <TooltippedIcon size="small" value={IconName.Info} tooltip={helper} />
        ) : null;

        const { readOnlyForm } = this.context;

        const selectProps = {
            ...other,
            virtualized: virtualized || itemCount >= 50,
            emptyMessage: <LocalizedText textKey={TextKey.NoMatch} />,
            multiselect,
            selected,
            name,
            onChange: this.handleChange,
            'data-test-select-name': name,
            captionIconWithTooltip,
            helper: bottomCaption,
            searchPlaceholder: <LocalizedText textKey={TextKey.Search} />,
            disabled: readOnlyForm || other?.disabled,
            selectAllEnabled: showSelectAll,
        };

        const resultItems =
            options && (isGroupOption(options) ? this.renderGroupItems(options) : this.renderSingleItems(options));

        const menuItems =
            Children.count(children) === 0
                ? resultItems
                : Children.map(children, (child) =>
                      isValidElement(child)
                          ? cloneElement(child, {
                                // @ts-expect-error $TSFixMeError: No overload matches this call.
                                'data-test-select-item-value': child.props.value,
                                key: child.props.value,
                            })
                          : null
                  );

        return (
            // @ts-expect-error $TSFixMeError: Type '{ children: any[]; virtualized: boolean; emptyMessage: Element; multiselect: boolean | undefined; selected: any; n...
            <OptimusSelect {...selectProps}>
                {!multiselect && showEmptyValue && (
                    <DropdownItem data-test-select-item-value="" value="" label={nullText} />
                )}
                {menuItems}
            </OptimusSelect>
        );
    }
}

Select.contextType = CommanderFormContext;
